export interface HangingProtocolSlotRule {
  viewer_index: number
  text_contains: string | null
}

export interface HangingProtocol {
  id: string
  name: string
  layout_key: string
  is_default: boolean
  slots: HangingProtocolSlotRule[]
  created_at?: string
  updated_at?: string
}

export interface SeriesMatchCandidate {
  id: string
  series_identifier: string
  series_description: string | null
  modality: string | null
  image_plane: string | null
  series_number: number | null
}

export interface HangingProtocolAssignment {
  viewerIndex: number
  seriesId: string
}

/**
 * Result of scoring a series against a slot rule. Higher `tier` wins; within
 * the same tier the candidate whose description length is closest to the
 * pattern (smaller `descLenDelta`) wins. `null` means the series does not
 * match the rule at all.
 *
 * Tiers:
 *   4 — `series_description` equals the pattern exactly (normalized)
 *   3 — pattern appears in `series_description` on a word boundary
 *   2 — pattern appears anywhere in `series_description`
 *   1 — pattern appears only in `series_identifier`
 *   0 — rule has no pattern (every series matches neutrally)
 */
export interface SlotMatchScore {
  tier: number
  descLenDelta: number
}

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase()
}

function isWordChar(char: string | undefined): boolean {
  if (!char) return false
  return /[a-z0-9]/.test(char)
}

function containsAsWord(haystack: string, needle: string): boolean {
  if (!(needle && haystack)) return false
  let from = 0
  while (from <= haystack.length - needle.length) {
    const index = haystack.indexOf(needle, from)
    if (index === -1) return false
    const before = index === 0 ? undefined : haystack[index - 1]
    const after = index + needle.length >= haystack.length ? undefined : haystack[index + needle.length]
    if (!(isWordChar(before) || isWordChar(after))) return true
    from = index + 1
  }
  return false
}

export function scoreSlotRule(series: SeriesMatchCandidate, rule: HangingProtocolSlotRule): SlotMatchScore | null {
  const pattern = normalize(rule.text_contains)
  const description = normalize(series.series_description)
  const identifier = normalize(series.series_identifier)

  if (!pattern) {
    // No filter: every series matches at a neutral tier. `descLenDelta` is a
    // constant so existing tiebreakers (series_number, list index) decide.
    return { tier: 0, descLenDelta: 0 }
  }

  const descLenDelta = Math.abs(description.length - pattern.length)

  if (description && description === pattern) {
    return { tier: 4, descLenDelta }
  }
  if (containsAsWord(description, pattern)) {
    return { tier: 3, descLenDelta }
  }
  if (description.includes(pattern)) {
    return { tier: 2, descLenDelta }
  }
  if (identifier.includes(pattern)) {
    return { tier: 1, descLenDelta }
  }
  return null
}

export function buildHangingProtocolAssignments(
  protocol: HangingProtocol,
  series: SeriesMatchCandidate[]
): HangingProtocolAssignment[] {
  const indexedSeries = series.map((item, index) => ({ item, index }))

  const assignments: HangingProtocolAssignment[] = []
  const sortedRules = [...protocol.slots].sort((a, b) => a.viewer_index - b.viewer_index)

  for (const rule of sortedRules) {
    const scored = indexedSeries
      .map(({ item, index }) => {
        const score = scoreSlotRule(item, rule)
        return score ? { item, index, score } : null
      })
      .filter((entry): entry is { item: SeriesMatchCandidate; index: number; score: SlotMatchScore } => entry !== null)
      .sort((a, b) => {
        if (a.score.tier !== b.score.tier) return b.score.tier - a.score.tier
        if (a.score.descLenDelta !== b.score.descLenDelta) return a.score.descLenDelta - b.score.descLenDelta
        const aSeries = a.item.series_number
        const bSeries = b.item.series_number
        if (aSeries == null && bSeries != null) return 1
        if (aSeries != null && bSeries == null) return -1
        if (aSeries != null && bSeries != null && aSeries !== bSeries) {
          return aSeries - bSeries
        }
        return a.index - b.index
      })

    const selected = scored[0]?.item
    if (!selected) continue
    assignments.push({
      viewerIndex: rule.viewer_index,
      seriesId: selected.id
    })
  }

  return assignments
}
